import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, ArrowLeft, Store as StoreIcon, Server, UserPlus } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const TYPE_OPTIONS = [
  { value: "type1", label: "Type 1 — TD_YYYYMMDD.txt (Landlord daily)" },
  { value: "type2", label: "Type 2 — Mall Hourly (e.g. Parkway Parade)" },
  { value: "type3", label: "Type 3 — DOS 8.3 (e.g. CapitaLand Option B)" },
  { value: "type4", label: "Type 4 — Subway Hourly (H{MID}_)" },
  { value: "type5", label: "Type 5 — Mall Hourly2 ({MID}{YYYYMMDD}.txt)" },
];

const GST_OPTIONS = [
  { value: "no", label: "No" },
  { value: "retail", label: "Yes — Retail (× 1.09)" },
  { value: "fnb", label: "Yes — F&B (× 1.199)" },
];

const FILE_TYPE_OPTIONS = [
  { value: "hourly_sales_cashcow", label: "Hourly Sales CashCow" },
  { value: "hourly_sales_thinkpos", label: "Hourly Sales ThinkPOS" },
];

const normalizeFileType = (value) =>
  value === "hourly_sales_thinkpos" ? value : "hourly_sales_cashcow";

export default function StoreForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    uid: "",
    tenant_id: "",
    email: "",
    phone: "",
    store_type: "type1",
    file_type: "hourly_sales_cashcow",
    has_gst: "no",
    notes: "",
    machine_id: "",
    ftp_host: "",
    is_sftp: false,
    ftp_username: "",
    ftp_password: "",
    ftp_port: 21,
    ftp_root_path: "/",
    active: true,
    // account
    account_username: "",
    account_password: "",
    account_name: "",
  });

  useEffect(() => {
    if (!isEdit) return;
    api
      .get(`/stores/${id}`)
      .then(({ data }) =>
        setForm((f) => ({ ...f, ...data, file_type: normalizeFileType(data.file_type) }))
      )
      .catch(() => toast.error("Failed to load store"))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const update = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value ?? e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isEdit && form.account_username && form.account_password) {
      // ok
    }
    setSaving(true);
    try {
      if (isEdit) {
        // strip account fields on edit
        const { account_username, account_password, account_name, ...payload } = form;
        await api.patch(`/stores/${id}`, payload);
        toast.success("Store updated");
      } else {
        await api.post(`/stores`, form);
        toast.success("Store created");
      }
      navigate("/stores");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="pg-card p-10 text-center text-slate-400">Loading...</div>;
  }

  return (
    <div data-testid="store-form-page">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-2 mb-4"
      >
        <ArrowLeft size={15} /> Back
      </button>
      <div className="mb-7">
        <div className="text-xs uppercase tracking-[0.32em] text-slate-400 mb-1">
          Stores · {isEdit ? "Edit" : "Create"}
        </div>
        <h1 className="text-3xl font-extrabold text-slate-900">
          {isEdit ? `Edit ${form.name}` : "Add new store"}
        </h1>
      </div>

      <form onSubmit={submit} className="space-y-6">
        {/* STORE DETAILS */}
        <section className="pg-card p-6 lg:p-8">
          <div className="field-section-title mb-5">
            <StoreIcon size={16} /> Store details
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Name *">
              <input className={inp} required value={form.name} onChange={update("name")} data-testid="f-name" />
            </Field>
            <Field label="UID / Unit no.">
              <input className={inp} value={form.uid} onChange={update("uid")} placeholder="#02-K03" />
            </Field>
            <Field label="Tenant ID">
              <input className={inp} value={form.tenant_id} onChange={update("tenant_id")} />
            </Field>
            <Field label="Email">
              <input className={inp} type="email" value={form.email} onChange={update("email")} />
            </Field>
            <Field label="Phone">
              <input className={inp} value={form.phone} onChange={update("phone")} />
            </Field>
            <Field label="Store type *">
              <select className={inp} required value={form.store_type} onChange={update("store_type")} data-testid="f-type">
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
            <Field label="File type *">
              <select className={inp} required value={form.file_type} onChange={update("file_type")} data-testid="f-file-type">
                {FILE_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Has GST?">
              <select className={inp} value={form.has_gst} onChange={update("has_gst")} data-testid="f-gst">
                {GST_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Machine ID *">
              <input className={inp} required value={form.machine_id} onChange={update("machine_id")} placeholder="6000459" data-testid="f-machine" />
            </Field>
            <Field label="Notes" className="md:col-span-3">
              <textarea className={`${inp} min-h-[80px]`} value={form.notes || ""} onChange={update("notes")} />
            </Field>
          </div>
        </section>

        {/* FTP DETAILS */}
        <section className="pg-card p-6 lg:p-8">
          <div className="field-section-title mb-5">
            <Server size={16} /> FTP details
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="FTP Host">
              <input className={inp} value={form.ftp_host} onChange={update("ftp_host")} placeholder="fplsinpossftp01.frasersproperty.com" />
            </Field>
            <Field label="Is sFTP Connection?">
              <select className={inp} value={String(form.is_sftp)} onChange={(e) => setForm((f) => ({ ...f, is_sftp: e.target.value === "true", ftp_port: e.target.value === "true" ? 22 : 21 }))}>
                <option value="false">No (regular FTP)</option>
                <option value="true">Yes (sFTP / SSH)</option>
              </select>
            </Field>
            <Field label="FTP Username">
              <input className={inp} value={form.ftp_username} onChange={update("ftp_username")} />
            </Field>
            <Field label="FTP Password">
              <input className={inp} type="password" value={form.ftp_password} onChange={update("ftp_password")} placeholder={isEdit ? "Leave empty to keep" : ""} />
            </Field>
            <Field label="FTP Port">
              <input className={inp} type="number" value={form.ftp_port} onChange={update("ftp_port")} />
            </Field>
            <Field label="FTP Root Path">
              <input className={inp} value={form.ftp_root_path} onChange={update("ftp_root_path")} placeholder="/" />
            </Field>
          </div>
        </section>

        {/* ACCOUNT DETAILS (only on create) */}
        {!isEdit && (
          <section className="pg-card p-6 lg:p-8">
            <div className="field-section-title mb-5">
              <UserPlus size={16} /> Account details (optional store user)
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Create a linked store user that can only access this store. Leave empty if not needed.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Username">
                <input className={inp} value={form.account_username} onChange={update("account_username")} />
              </Field>
              <Field label="Password">
                <input className={inp} type="password" value={form.account_password} onChange={update("account_password")} />
              </Field>
              <Field label="Display name">
                <input className={inp} value={form.account_name} onChange={update("account_name")} />
              </Field>
            </div>
          </section>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-5 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-60"
            data-testid="save-store-btn"
          >
            <Save size={15} />
            {saving ? "Saving..." : isEdit ? "Update store" : "Create store"}
          </button>
        </div>
      </form>
    </div>
  );
}

const inp =
  "w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none transition bg-white text-sm";

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}
